import { useEffect, useRef, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Receipt, ArrowRight, Funnel, Trash, Clock, WarningOctagon, MagnifyingGlass, X as XIcon } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatMoney, formatDate } from "../lib/format";
import PageHeader from "../components/layout/PageHeader";
import EmptyState from "../components/EmptyState";
import CopyCodeButton from "../components/CopyCodeButton";
import CurrencySelect from "../components/CurrencySelect";
import StatusBadge from "../components/StatusBadge";
import RoleGuard from "../components/RoleGuard";
import SearchableSelect from "../components/SearchableSelect";
import DateFilterButton from "../components/DateFilterButton";
import FormDialog, { FormDialogCancel } from "../components/FormDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { PinnedScrollbar } from "../components/PinnedScrollbar";

function NewInvoiceDialog({ clients, onCreated }) {
    const [open, setOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const initialForm = { client_id: "", billing_start_date: "", billing_end_date: "", payment_terms: "30 days", notes: "", currency: "INR" };
    const initialLines = [{ description: "", hsn_sac_code: "998314", amount: 0, gst_percent: 18 }];
    const [form, setForm] = useState(initialForm);
    const [lines, setLines] = useState(initialLines);
    const baselineRef = useRef(JSON.stringify({ form: initialForm, lines: initialLines }));

    useEffect(() => {
        if (open) {
            setForm(initialForm);
            setLines(initialLines);
            baselineRef.current = JSON.stringify({ form: initialForm, lines: initialLines });
        }
        // eslint-disable-next-line
    }, [open]);

    const isDirty = JSON.stringify({ form, lines }) !== baselineRef.current;

    const updateLine = (idx, key, value) =>
        setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [key]: value } : l)));
    const addLine = () => setLines((prev) => [...prev, { description: "", hsn_sac_code: "998314", amount: 0, gst_percent: 18 }]);
    const removeLine = (idx) => setLines((prev) => prev.filter((_, i) => i !== idx));

    const subtotal = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
    const tax = lines.reduce((s, l) => s + ((Number(l.amount) || 0) * (Number(l.gst_percent) || 0) / 100), 0);
    const total = subtotal + tax;

    const submit = async () => {
        if (!form.client_id) { toast.error("Pick a client"); return; }
        if (!lines.length || lines.some((l) => !l.description || !Number(l.amount))) {
            toast.error("Add at least one valid line item");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                ...form,
                line_items: lines.map((l) => ({ ...l, amount: Number(l.amount), gst_percent: Number(l.gst_percent) || 0 })),
            };
            const { data } = await api.post("/billing/invoice/create", payload);
            toast.success(`Invoice ${data.invoice_number} created`);
            baselineRef.current = JSON.stringify({ form, lines });  // bypass discard prompt
            setOpen(false);
            setLines([{ description: "", hsn_sac_code: "998314", amount: 0, gst_percent: 18 }]);
            setForm({ client_id: "", billing_start_date: "", billing_end_date: "", payment_terms: "30 days", notes: "", currency: "INR" });
            onCreated?.(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create invoice"));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <Button
                data-testid="new-invoice-btn"
                onClick={() => setOpen(true)}
                className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5"
            >
                <Plus size={16} weight="bold" /> New invoice
            </Button>
            <FormDialog
                open={open}
                onOpenChange={setOpen}
                isDirty={isDirty}
                contentClassName="max-w-2xl"
                testId="new-invoice-dialog"
                title="New invoice (manual)"
                description="Invoices are immutable once created. Use credit notes (future) for corrections."
            >
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Client</Label>
                        <SearchableSelect
                            options={clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))}
                            value={form.client_id}
                            onChange={(v) => setForm({ ...form, client_id: v })}
                            placeholder="— Select client —"
                            searchPlaceholder="Type to filter clients…"
                            emptyText="No matching client."
                            testId="manual-inv-client"
                            className="mt-1.5"
                        />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Payment terms</Label>
                        <select value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} className="mt-1.5 w-full h-10 px-3 text-sm border border-slate-200 rounded-md bg-white">
                            <option>Immediate</option>
                            <option>15 days</option>
                            <option>30 days</option>
                            <option>45 days</option>
                        </select>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs font-semibold">Billing start</Label>
                        <Input type="date" value={form.billing_start_date} onChange={(e) => setForm({ ...form, billing_start_date: e.target.value })} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold">Billing end</Label>
                        <Input type="date" value={form.billing_end_date} onChange={(e) => setForm({ ...form, billing_end_date: e.target.value })} className="mt-1.5" />
                    </div>
                </div>
                <CurrencySelect
                    value={form.currency}
                    onChange={(v) => setForm({ ...form, currency: v })}
                    testid="invoice-currency-select"
                />
                <div>
                    <Label className="text-xs font-semibold">Line items</Label>
                    <div className="mt-1.5 space-y-2" data-testid="manual-inv-lines">
                        {lines.map((l, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                                <Input className="col-span-5" placeholder="Description" value={l.description} onChange={(e) => updateLine(idx, "description", e.target.value)} data-testid={`mline-desc-${idx}`} />
                                <Input className="col-span-2 font-mono" placeholder="HSN/SAC" value={l.hsn_sac_code} onChange={(e) => updateLine(idx, "hsn_sac_code", e.target.value)} data-testid={`mline-hsn-${idx}`} />
                                <Input className="col-span-2 font-mono" type="number" min="0" step="0.01" placeholder="Amount" value={l.amount} onChange={(e) => updateLine(idx, "amount", e.target.value)} data-testid={`mline-amt-${idx}`} />
                                <Input className="col-span-2 font-mono" type="number" min="0" max="28" step="0.01" placeholder="GST%" value={l.gst_percent} onChange={(e) => updateLine(idx, "gst_percent", e.target.value)} data-testid={`mline-gst-${idx}`} />
                                <Button type="button" size="icon" variant="ghost" disabled={lines.length === 1} onClick={() => removeLine(idx)} className="col-span-1 h-9 text-slate-400 hover:text-rose-600" data-testid={`mline-remove-${idx}`}>
                                    <Trash size={14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                    <Button variant="outline" size="sm" type="button" onClick={addLine} className="mt-2 gap-1.5" data-testid="mline-add-btn">
                        <Plus size={12} /> Add line
                    </Button>
                </div>
                <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-md p-3 reedos-data">
                    <div className="text-xs"><div className="text-slate-500">Subtotal</div><div className="font-semibold">{formatINR(subtotal)}</div></div>
                    <div className="text-xs"><div className="text-slate-500">GST</div><div className="font-semibold">{formatINR(tax)}</div></div>
                    <div className="text-xs"><div className="text-slate-500">Total</div><div className="font-display font-bold text-slate-900">{formatINR(total)}</div></div>
                </div>
                <div>
                    <Label className="text-xs font-semibold">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5" />
                </div>
                <DialogFooter>
                    <FormDialogCancel testId="manual-inv-cancel">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="manual-inv-submit">
                        {submitting ? "Creating..." : "Create invoice"}
                    </Button>
                </DialogFooter>
            </FormDialog>
        </>
    );
}

export default function Invoices() {
    const tableScrollRef = useRef(null);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
    const [clientFilter, setClientFilter] = useState(searchParams.get("client") || "");
    const [overdueOnly, setOverdueOnly] = useState(searchParams.get("overdue") === "true");
    const [sort, setSort] = useState(searchParams.get("sort") || "date_desc");
    const [textQuery, setTextQuery] = useState("");
    // Iter 2026-02-27 · Invoice date-range filters (Raised + Due).
    // ISO YYYY-MM-DD strings, inclusive on both bounds. Survive refresh and
    // link-sharing via URL params (`date_from`/`date_to`/`due_date_from`/`due_date_to`)
    // that match the backend contract 1:1.
    const [raisedFrom, setRaisedFrom] = useState(searchParams.get("date_from") || "");
    const [raisedTo, setRaisedTo] = useState(searchParams.get("date_to") || "");
    const [dueFrom, setDueFrom] = useState(searchParams.get("due_date_from") || "");
    const [dueTo, setDueTo] = useState(searchParams.get("due_date_to") || "");
    // Client-side validation guard — From > To is rejected before the request
    // reaches the backend. Toast + skip the load; server never sees an inverted range.
    const raisedRangeInverted = !!(raisedFrom && raisedTo && raisedFrom > raisedTo);
    const dueRangeInverted = !!(dueFrom && dueTo && dueFrom > dueTo);

    const visibleItems = useMemo(() => {
        const q = textQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter((i) => {
            const num = (i.invoice_number || "").toLowerCase();
            const ext = (i.external_invoice_number || "").toLowerCase();
            const cli = (i.client?.name || "").toLowerCase();
            const notes = (i.notes || "").toLowerCase();
            // Iter 41: extend the client-side filter to include Client PO number
            // so the same search field works for the PO↔Invoice mapping use case.
            const po = (i.external_po_number || "").toLowerCase();
            return num.includes(q) || ext.includes(q) || po.includes(q) || cli.includes(q) || notes.includes(q);
        });
    }, [items, textQuery]);

    const load = async () => {
        // Validation gate — do not hit the API with an inverted range.
        // Surface a targeted toast so the operator understands which range failed.
        if (raisedRangeInverted) {
            toast.error("Raised Date — From cannot be later than To.");
            setLoading(false);
            return;
        }
        if (dueRangeInverted) {
            toast.error("Due Date — From cannot be later than To.");
            setLoading(false);
            return;
        }
        try {
            const params = { sort };
            if (statusFilter) params.status = statusFilter;
            if (clientFilter) params.client_id = clientFilter;
            if (overdueOnly) params.overdue = true;
            if (raisedFrom) params.date_from = raisedFrom;
            if (raisedTo) params.date_to = raisedTo;
            if (dueFrom) params.due_date_from = dueFrom;
            if (dueTo) params.due_date_to = dueTo;
            const [inv, cls] = await Promise.all([
                api.get("/billing/invoice/list", { params }),
                api.get("/billing/client/list"),
            ]);
            setItems(inv.data.items || []);
            setClients(cls.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load invoices"));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, clientFilter, overdueOnly, sort, raisedFrom, raisedTo, dueFrom, dueTo]);

    useEffect(() => {
        const sp = new URLSearchParams();
        if (statusFilter) sp.set("status", statusFilter);
        if (clientFilter) sp.set("client", clientFilter);
        if (overdueOnly) sp.set("overdue", "true");
        if (sort !== "date_desc") sp.set("sort", sort);
        if (raisedFrom) sp.set("date_from", raisedFrom);
        if (raisedTo) sp.set("date_to", raisedTo);
        if (dueFrom) sp.set("due_date_from", dueFrom);
        if (dueTo) sp.set("due_date_to", dueTo);
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, clientFilter, overdueOnly, sort, raisedFrom, raisedTo, dueFrom, dueTo]);

    // Clear-all reset — nukes every invoice-list filter back to first-load defaults
    // WITHOUT touching unrelated app state (no navigate, no toast spam, no
    // NewInvoiceDialog reset). URL params drop via the round-trip effect above.
    const hasAnyFilter = !!(
        statusFilter || clientFilter || overdueOnly ||
        raisedFrom || raisedTo || dueFrom || dueTo ||
        textQuery || sort !== "date_desc"
    );
    const clearAllFilters = () => {
        setStatusFilter("");
        setClientFilter("");
        setOverdueOnly(false);
        setSort("date_desc");
        setTextQuery("");
        setRaisedFrom("");
        setRaisedTo("");
        setDueFrom("");
        setDueTo("");
    };

    return (
        <div data-testid="invoices-page">
            <PageHeader
                eyebrow="Accounts"
                title="Invoices"
                description="All invoices issued — immutable after creation. Every invoice derives from a finalized PO. Open a PO to raise an advance / milestone / final invoice."
                actions={null}
            />

            <div className="px-6 sm:px-8 py-8">
                <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="invoice-filters">
                    <div className="relative flex-1 min-w-[220px] max-w-md">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            value={textQuery}
                            onChange={(e) => setTextQuery(e.target.value)}
                            placeholder="Search invoice #, external #, client, client PO, or notes…"
                            className="pl-8 h-9 text-sm"
                            data-testid="invoices-search-input"
                        />
                    </div>
                    <div className="reedos-eyebrow flex items-center gap-1.5"><Funnel size={12} /> Filter</div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="invoice-status-filter">
                        <option value="">All status</option>
                        <option value="Unpaid">Unpaid</option>
                        <option value="Partial">Partial</option>
                        <option value="Paid">Paid</option>
                    </select>
                    <div className="w-56">
                        <SearchableSelect
                            options={[{ value: "", label: "All clients" }, ...clients.map((c) => ({ value: c.id, label: c.name, hint: c.gstin || undefined }))]}
                            value={clientFilter}
                            onChange={setClientFilter}
                            placeholder="All clients"
                            searchPlaceholder="Filter clients…"
                            emptyText="No matching client."
                            testId="invoice-client-filter"
                            className="!h-9"
                        />
                    </div>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 h-9 px-3 border rounded-md bg-white cursor-pointer" data-testid="invoice-overdue-toggle-label">
                        <input
                            type="checkbox"
                            checked={overdueOnly}
                            onChange={(e) => setOverdueOnly(e.target.checked)}
                            data-testid="invoice-overdue-toggle"
                        />
                        <span className="inline-flex items-center gap-1">
                            <WarningOctagon size={12} weight={overdueOnly ? "fill" : "regular"} className={overdueOnly ? "text-rose-600" : ""} />
                            Overdue only
                        </span>
                    </label>
                    <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 px-3 text-sm border border-slate-200 rounded-md bg-white" data-testid="invoice-sort">
                        <option value="date_desc">Newest first</option>
                        <option value="date_asc">Oldest first</option>
                        <option value="overdue_desc">Most overdue first</option>
                        <option value="amount_desc">Largest amount first</option>
                    </select>
                </div>

                {/* Iter 2026-02-27 · Date-range filter row — Raised + Due, inclusive both
                    ends, composes with all sibling filters. From > To is rejected client-side
                    with an inline hint + skip-load, so the backend never sees an inverted range. */}
                <div className="flex flex-wrap items-end gap-3 mb-5" data-testid="invoice-date-filters">
                    <div className="w-40">
                        <DateFilterButton
                            label="Raised Date — From"
                            value={raisedFrom}
                            onChange={setRaisedFrom}
                            placeholder="Any date"
                            testid="invoice-raised-from-filter"
                        />
                    </div>
                    <div className="w-40">
                        <DateFilterButton
                            label="Raised Date — To"
                            value={raisedTo}
                            onChange={setRaisedTo}
                            placeholder="Any date"
                            testid="invoice-raised-to-filter"
                        />
                    </div>
                    <div className="h-8 w-px bg-slate-200 self-end mb-1" aria-hidden />
                    <div className="w-40">
                        <DateFilterButton
                            label="Due Date — From"
                            value={dueFrom}
                            onChange={setDueFrom}
                            placeholder="Any date"
                            testid="invoice-due-from-filter"
                        />
                    </div>
                    <div className="w-40">
                        <DateFilterButton
                            label="Due Date — To"
                            value={dueTo}
                            onChange={setDueTo}
                            placeholder="Any date"
                            testid="invoice-due-to-filter"
                        />
                    </div>
                    {hasAnyFilter && (
                        <button
                            type="button"
                            onClick={clearAllFilters}
                            className="ml-auto h-8 inline-flex items-center gap-1.5 px-3 text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md border border-slate-200 self-end mb-1"
                            data-testid="invoice-clear-filters"
                        >
                            <XIcon size={12} /> Clear filters
                        </button>
                    )}
                </div>
                {(raisedRangeInverted || dueRangeInverted) && (
                    <div
                        className="mb-4 px-3 py-2 text-xs font-medium text-rose-700 bg-rose-50 border border-rose-200 rounded-md"
                        data-testid="invoice-date-range-error"
                    >
                        {raisedRangeInverted && <div>Raised Date — From cannot be later than To.</div>}
                        {dueRangeInverted && <div>Due Date — From cannot be later than To.</div>}
                    </div>
                )}

                {loading ? (
                    <div className="text-sm text-slate-500">Loading invoices...</div>
                ) : visibleItems.length === 0 ? (
                    <EmptyState
                        icon={<Receipt size={40} weight="thin" />}
                        title="No invoices match"
                        description="Change filters or create a new invoice."
                        data-testid="invoices-empty"
                    />
                ) : (
                    <div className="reedos-card overflow-hidden">
                        <div className="overflow-x-auto" ref={tableScrollRef} data-testid="invoices-table-scroll">
                        <table className="w-full text-sm" data-testid="invoices-table">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr className="text-left">
                                    <th className="px-5 py-3 reedos-eyebrow">Invoice #</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Client</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Issued</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Due</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right">Total</th>
                                    <th className="px-5 py-3 reedos-eyebrow text-right" title="Money the client still owes in cash. Excludes pending TDS reconciliation (paperwork, not a receivable).">Collection Outstanding</th>
                                    <th className="px-5 py-3 reedos-eyebrow">Status</th>
                                    <th className="px-5 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((i) => (
                                    <tr
                                        key={i.id}
                                        className={"border-t border-slate-100 reedos-row-hover " + (i.is_overdue ? "bg-rose-50/30" : "")}
                                        data-testid={`invoice-row-${i.id}`}
                                    >
                                        <td className="px-5 py-3">
                                            <span className="inline-flex items-center">
                                                <Link to={`/invoices/${i.id}`} data-testid={`invoice-open-${i.id}`} className="font-mono text-xs font-semibold text-slate-900 hover:underline">
                                                    {i.invoice_number}
                                                </Link>
                                                <CopyCodeButton
                                                    code={i.invoice_number}
                                                    label="invoice number"
                                                    testid={`invoice-number-copy-${i.id}`}
                                                />
                                            </span>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {i.invoice_label && <span className="inline-flex items-center rounded px-1 py-0 text-[9px] font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200 mr-1.5">{i.invoice_label}</span>}
                                                {i.billing_source === "offline_recorded" && (
                                                    <span
                                                        className="inline-flex items-center rounded px-1 py-0 text-[9px] font-semibold border bg-amber-50 text-amber-700 border-amber-200 mr-1.5"
                                                        title={i.external_invoice_number ? `External ref: ${i.external_invoice_number}` : "Recorded from an external system"}
                                                        data-testid={`invoice-offline-chip-${i.id}`}
                                                    >
                                                        Offline
                                                    </span>
                                                )}
                                                {i.payment_terms}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3 text-slate-900">{i.client?.name}</td>
                                        <td className="px-5 py-3 text-xs text-slate-600">{formatDate(i.invoice_date)}</td>
                                        <td className="px-5 py-3 text-xs">
                                            <div className="text-slate-600">{formatDate(i.due_date)}</div>
                                            {i.is_overdue && (
                                                <div className="inline-flex items-center gap-1 text-rose-700 font-semibold text-[11px] mt-0.5">
                                                    <Clock size={10} weight="fill" /> {i.days_overdue}d overdue
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right reedos-data font-semibold">{formatMoney(i.total_amount, i.currency)}</td>
                                        <td className={"px-5 py-3 text-right reedos-data font-semibold " + ((i.collection_outstanding ?? i.outstanding) > 0 ? "text-rose-700" : "text-slate-400")}>
                                            {formatMoney(i.collection_outstanding ?? i.outstanding, i.currency)}
                                            {Number(i.pending_tds_reconciliation) > 0.01 && (
                                                <div
                                                    className="text-[9px] font-medium text-teal-700 font-mono mt-0.5"
                                                    title="TDS recorded (status Deducted / Received) — pending verification. Not a client receivable."
                                                    data-testid={`invoice-row-pending-tds-${i.id}`}
                                                >
                                                    + {formatMoney(i.pending_tds_reconciliation, i.currency)} pending TDS
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3"><StatusBadge status={i.status} compact /></td>
                                        <td className="px-5 py-3 text-right">
                                            <Link to={`/invoices/${i.id}`} className="text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 text-xs font-medium">
                                                Open <ArrowRight size={12} weight="bold" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                        <PinnedScrollbar targetRef={tableScrollRef} testid="invoices-pinned-scrollbar" />
                    </div>
                )}
            </div>
        </div>
    );
}
